/*
 * Naturlabs O nás a Věda a kvalita (sekce nl-brand-*). Vanilla, bez závislostí, defer.
 *
 * <nl-brand-lineup>  sestava dóz: najetí, focus nebo tap otevře bublinu (aria-expanded), Esc a klik mimo zavře
 * <nl-brand-tabs>    přepínač produktů (role tablist): klik, šipky vlevo a vpravo, Home a End
 * <nl-brand-claims>  rejstřík tvrzení: filtr oblastí (aria-pressed), živý souhrn, akordeony, ztlumení tvrzení
 * [data-brand-video] smyčka videa hraje jen ve viewportu, ne při prefers-reduced-motion ani Save-Data
 *
 * Bez JS: první panel specimenu viditelný, rejstřík s filtrem „Vše“, bubliny dóz skryté (dózy vedou přes alt).
 */
(() => {
  'use strict';

  if (window.NLBrand) return;
  window.NLBrand = true;

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  const reduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.classList.contains('nl-motion-off');
  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const desktop = () => window.matchMedia('(min-width: 1024px)').matches;

  /* ------------------------------------------------------------------ */
  /* Sestava dóz                                                         */
  /* ------------------------------------------------------------------ */
  class BrandLineup extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.buttons = Array.from(this.querySelectorAll('[data-lineup-btn]'));
      this.active = null;
      this.closeTimer = 0;

      this.buttons.forEach((btn) => {
        const bubble = this.bubbleFor(btn);
        /* Klik a tap bublinu vždy otevřou (nikdy nezavírají), zavírá Esc, klik mimo nebo odjetí myší */
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(btn);
        });
        btn.addEventListener('focus', () => {
          let keyboard = true;
          try {
            keyboard = btn.matches(':focus-visible');
          } catch (e) {
            keyboard = true;
          }
          if (keyboard) this.open(btn, true);
        });
        btn.addEventListener('mouseenter', () => {
          if (finePointer() && desktop()) this.open(btn);
        });
        btn.addEventListener('mouseleave', () => this.scheduleClose());
        if (bubble) {
          bubble.addEventListener('mouseenter', () => window.clearTimeout(this.closeTimer));
          bubble.addEventListener('mouseleave', () => this.scheduleClose());
          bubble.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget) && desktop()) this.close();
          });
        }
      });

      this.onKey = (event) => {
        if (event.key === 'Escape' && this.active && desktop()) {
          const btn = this.active;
          this.close();
          btn.focus({ preventScroll: true });
        }
      };
      this.onDocClick = (event) => {
        if (this.active && desktop() && !this.contains(event.target)) this.close();
      };
      this.onResize = () => {
        if (this.active) this.position(this.active);
      };
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('resize', this.onResize, { passive: true });

      /* Na mobilu a tabletu je karta pod řadou vidět hned (první dóza) */
      if (!desktop() && this.buttons[0]) this.open(this.buttons[0], true);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('resize', this.onResize);
    }

    bubbleFor(btn) {
      const id = btn.getAttribute('aria-controls');
      return id ? document.getElementById(id) : null;
    }

    open(btn, silent) {
      window.clearTimeout(this.closeTimer);
      if (this.active && this.active !== btn) this.hide(this.active);
      const bubble = this.bubbleFor(btn);
      if (!bubble) return;
      btn.setAttribute('aria-expanded', 'true');
      bubble.hidden = false;
      this.active = btn;
      this.position(btn);
      if (!silent && !desktop()) {
        const row = btn.closest('.nl-brand-lineup__row');
        const item = btn.closest('li');
        if (row && item) {
          const left = item.offsetLeft - (row.clientWidth - item.clientWidth) / 2;
          row.scrollTo({ left, behavior: reduced() ? 'auto' : 'smooth' });
        }
      }
    }

    hide(btn) {
      btn.setAttribute('aria-expanded', 'false');
      const bubble = this.bubbleFor(btn);
      if (bubble) bubble.hidden = true;
    }

    close() {
      if (!this.active) return;
      this.hide(this.active);
      this.active = null;
    }

    scheduleClose() {
      if (!(finePointer() && desktop())) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        if (this.active && document.activeElement && this.contains(document.activeElement)) return;
        this.close();
      }, 220);
    }

    position(btn) {
      const bubble = this.bubbleFor(btn);
      if (!bubble) return;
      if (!desktop()) {
        bubble.style.removeProperty('--bubble-left');
        bubble.style.removeProperty('--bubble-top');
        return;
      }
      const stage = this.getBoundingClientRect();
      const jar = (btn.querySelector('.nl-brand-jar') || btn).getBoundingClientRect();
      const half = Math.min(bubble.offsetWidth || 300, stage.width) / 2;
      let left = jar.left - stage.left + jar.width / 2;
      left = Math.max(half, Math.min(stage.width - half, left));
      bubble.style.setProperty('--bubble-left', `${Math.round(left)}px`);
      bubble.style.setProperty('--bubble-top', `${Math.round(jar.top - stage.top + jar.height * 0.06)}px`);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Přepínač produktů (Jak číst složení)                                */
  /* ------------------------------------------------------------------ */
  class BrandTabs extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, true));
        tab.addEventListener('keydown', (event) => {
          let next = null;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          this.select(next, true);
        });
      });
    }

    select(index, focus) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
        if (on && focus) {
          tab.focus({ preventScroll: true });
          tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
        }
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Rejstřík tvrzení                                                    */
  /* ------------------------------------------------------------------ */
  const plural = (n, forms) => {
    const f = forms.map((s) => s.trim());
    if (n === 1) return f[0];
    if (n >= 2 && n <= 4) return f[1];
    return f[2] || f[1] || f[0];
  };

  class BrandClaims extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.pills = Array.from(this.querySelectorAll('[data-filter]'));
      this.rows = Array.from(this.querySelectorAll('.nl-brand-claims__row'));
      this.summary = this.querySelector('[data-summary]');
      this.empty = this.querySelector('[data-empty]');
      this.formsP = (this.dataset.formsProduct || 'produkt, produkty, produktů').split(',');
      this.formsC = (this.dataset.formsClaim || 'tvrzení, tvrzení, tvrzení').split(',');
      this.area = '';

      this.pills.forEach((pill) => {
        pill.addEventListener('click', () => this.filter(pill.dataset.filter || ''));
      });

      this.rows.forEach((row) => {
        const toggle = row.querySelector('[data-acc-toggle]');
        const head = row.querySelector('[data-acc-head]');
        if (!toggle) return;
        toggle.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggle(row);
        });
        if (head) {
          head.addEventListener('click', (event) => {
            if (event.target.closest('a, button')) return;
            this.toggle(row);
          });
        }
      });

      /* Hash #tvrzeni-<oblast> předvybere filtr (odkazy z jiných stránek) */
      const match = window.location.hash.match(/^#tvrzeni-([a-z-]+)$/);
      if (match && this.pills.some((p) => p.dataset.filter === match[1])) this.filter(match[1]);
    }

    toggle(row, force) {
      const toggle = row.querySelector('[data-acc-toggle]');
      const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
      if (!toggle || !panel) return;
      const open = typeof force === 'boolean' ? force : toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
      const acc = row.querySelector('.nl-brand-acc');
      if (acc) acc.classList.toggle('is-open', open);
    }

    filter(area) {
      this.area = area;
      this.pills.forEach((pill) => pill.setAttribute('aria-pressed', (pill.dataset.filter || '') === area ? 'true' : 'false'));
      let products = 0;
      this.rows.forEach((row) => {
        const areas = ` ${row.dataset.areas || ''} `;
        const count = Number(row.dataset.count || 0);
        const visible = area === '' ? true : areas.includes(` ${area} `);
        row.hidden = !visible;
        row.querySelectorAll('.nl-claim__item').forEach((item) => {
          item.classList.toggle('is-dim', area !== '' && item.dataset.area !== area);
        });
        row.querySelectorAll('.nl-brand-acc__areas li').forEach((chip) => {
          chip.classList.toggle('is-match', area !== '' && chip.dataset.area === area);
        });
        // final T1: s vybranou oblastí ukáže zavřený řádek první tvrzení této oblasti (štítek nikdy bez tvrzení)
        const match = row.querySelector('[data-acc-match]');
        if (match) {
          const base = row.querySelector('.nl-brand-acc__claim:not([data-acc-match])');
          const hit = area !== '' && visible ? row.querySelector(`.nl-claim__item[data-area="${area}"] > span:last-child`) : null;
          const text = hit ? hit.textContent.trim() : '';
          // UX 17. 10.: obě věty mají pevné mezery (nl-nbsp), porovnávají se se sjednocenými mezerami
          const norm = (s) => s.replace(/\s+/g, ' ').trim();
          const same = text !== '' && base && norm(base.textContent).includes(norm(text).replace(/\.$/, ''));
          const show = text !== '' && !same;
          match.textContent = show ? text : '';
          match.hidden = !show;
          if (base) base.hidden = show;
        }
        if (!visible) return;
        // polish-v3 C9: products without authorised claims (MSM) do not count in the summary
        if (count > 0) products += 1;
      });
      if (this.summary) {
        this.summary.textContent = `${products} ${plural(products, this.formsP)}`;
      }
      if (this.empty) this.empty.hidden = products > 0;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Smyčka videa                                                        */
  /* ------------------------------------------------------------------ */
  const bindVideos = (root) => {
    const saveData = navigator.connection && navigator.connection.saveData;
    root.querySelectorAll('[data-brand-video]').forEach((video) => {
      if (video.dataset.bound) return;
      video.dataset.bound = '1';
      if (reduced() || saveData || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
              if (video.preload === 'none') video.preload = 'auto';
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.4] }
      );
      io.observe(video);
    });
  };

  define('nl-brand-lineup', BrandLineup);
  define('nl-brand-tabs', BrandTabs);
  define('nl-brand-claims', BrandClaims);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindVideos(document));
  } else {
    bindVideos(document);
  }
  document.addEventListener('shopify:section:load', (event) => bindVideos(event.target));
})();
